import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Box,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { getSolicitationNumbersFromStore, storeSolicitationNumbers, removeSolicitationNumbersFromStore } from '../../utils/LocalStorageManager';

const SolNumAccordion = forwardRef(({ keywords, setKeywords }, ref) => {
  const [solNum, setSolNum] = useState(''); // Single input for solicitation number
  const [solNumList, setSolNumList] = useState([]); // List of added solicitation numbers

  useEffect(() => {
    // Retrieve keywords from local storage if they exist
    const solNumList = getSolicitationNumbersFromStore();
    if (solNumList && Array.isArray(solNumList)) {
      setSolNumList(solNumList);
    }
  }, [setKeywords]);

  // Add a new solicitation number, lowercased, to the list
  const addKeywords = (text) => {
    const lowercasedKeyword = text.toLowerCase();
    setKeywords((prev) => [...prev, lowercasedKeyword]);
    
    // Update solNumList and store the updated array
    setSolNumList((prev) => {
        const updatedList = [...prev, lowercasedKeyword];
        storeSolicitationNumbers(updatedList); // Pass the updated list to storeSolicitationNumbers
        return updatedList;
    });
  };

  // Handle adding a solicitation number from the input field
  const handleAddSolNum = () => {
    if (solNum.trim() !== '') {
        const cleanedSolNum = solNum.trim().toLowerCase();
        addKeywords(cleanedSolNum); // Add the cleaned solicitation number to the list
        setSolNum(''); // Clear the input field
    }
  };

  // Handle clearing the input field
  const handleClearInput = () => {
    setSolNum('');
  };

  // Handle removing a solicitation number from the list and keywords
  const handleRemoveSolNum = (removeItem) => {
    setSolNumList((prev) => prev.filter((item) => item !== removeItem));
    const cleanSolNums = (prev) => prev.filter((kw) => !removeItem.includes(kw));
    setKeywords(cleanSolNums);
    storeSolicitationNumbers(cleanSolNums);
  };

  // Expose reset functionality to parent component
  useImperativeHandle(ref, () => ({
    resetSolNumList: () => {
      setSolNumList([]); // Clear all solicitation numbers
      setKeywords((prev) => prev.filter((kw) => !solNumList.includes(kw))); // Clear related keywords
      removeSolicitationNumbersFromStore();
    }
  }));

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ backgroundColor: '#f5f5f5' }} // Light gray background for consistency
      >
        {/* Circle badge showing number of added solicitation numbers */}
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: solNumList.length === 0 ? 'gray' : '#00274D', // Gray if 0, navy blue otherwise
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'white' }}>
            {solNumList.length}
          </Typography>
        </Box>
        <Typography>Solicitation Number</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {/* Input Field */}
          <TextField
            fullWidth
            label="Enter solicitation number"
            value={solNum}
            onChange={(e) => setSolNum(e.target.value)}
          />

          {/* Clear Input Button */}
          <IconButton onClick={handleClearInput} sx={{ ml: 1 }}>
            <ClearIcon />
          </IconButton>

          {/* Add Button */}
          <IconButton onClick={handleAddSolNum} sx={{ ml: 1 }}>
            <AddIcon />
          </IconButton>
        </Box>

        {/* List of added solicitation numbers */}
        <List>
          {solNumList.map((sol, index) => (
            <ListItem key={index}>
              <ListItemText primary={sol} />
              <ListItemSecondaryAction>
                {/* Remove Button */}
                <IconButton edge="end" onClick={() => handleRemoveSolNum(sol)}>
                  <CloseIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
});

export default SolNumAccordion;